/**
 * URL Authentication Handler
 * Handles authentication via URL parameters for external app integration
 */

import { getAuthToken } from '../Action/api';

/**
 * Decode JWT token to extract payload data
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded token payload or null if invalid
 */
const decodeJWT = (token) => {
  try {
    // JWT has three parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Replace URL-safe characters
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    // Decode base64
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Extract user data from authToken stored in localStorage
 * This is a common method to get user information from the JWT token
 * @returns {Object} User data object with all fields, or empty strings if token is invalid
 */
export const getUserDataFromToken = () => {
  // Default empty object in case token is not available or invalid
  const defaultData = {
    isAuthenticated: false,
    userId: '',
    userName: '',
    staffId: '',
    roleId: '',
    roleName: '',
    depId: '',
    depName: '',
    gradeCode: '',
    emailId: '',
    uId: '',
    loginTime: null
  };

  try {
    // Get authToken from localStorage
    const authToken = getAuthToken();
    
    if (!authToken) {
      console.warn('No authToken found in localStorage');
      return defaultData;
    }

    // Decode the JWT token
    const decodedToken = decodeJWT(authToken);
    
    if (!decodedToken) {
      console.error('Failed to decode authToken');
      return defaultData;
    }

    // Check token expiration if 'exp' field exists
    const isTokenValid = decodedToken.exp ? decodedToken.exp * 1000 > Date.now() : true;

    // Extract login time from token's 'iat' (issued at) field or custom loginTime field
    // iat is in seconds, convert to milliseconds
    const loginTimeMs = decodedToken.loginTime || 
                       (decodedToken.iat ? decodedToken.iat * 1000 : null);

    // Extract and return user data from decoded token
    return {
      isAuthenticated: isTokenValid,
      userId: decodedToken.userId || decodedToken.uId || decodedToken.sub || '',
      userName: decodedToken.userName || decodedToken.name || decodedToken.username || '',
      staffId: decodedToken.staffId || decodedToken.uId || decodedToken.userId || '',
      roleId: decodedToken.roleId || decodedToken.role || '',
      roleName: decodedToken.roleName || '',
      depId: decodedToken.depId || decodedToken.departmentId || '',
      depName: decodedToken.depName || decodedToken.departmentName || '',
      gradeCode: decodedToken.gradeCode || decodedToken.grade || '',
      emailId: decodedToken.emailId || decodedToken.email || '',
      uId: decodedToken.uId || decodedToken.userId || '',
      loginTime: loginTimeMs
    };
  } catch (error) {
    console.error('Error extracting user data from token:', error);
    return defaultData;
  }
};

/**
 * Extract authentication parameters from URL
 * @returns {Object|null} Authentication data or null if not present
 */
export const extractAuthFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  const token = urlParams.get('token');

  // Check if token is present
  if (!token) {
    return null;
  }

  // Decode JWT to extract user data
  const decodedToken = decodeJWT(token);
  
  if (!decodedToken) {
    console.error('Failed to decode token');
    return null;
  }

  // Log decoded token for debugging
  console.log('Decoded JWT Token:', decodedToken);

  // Extract data from decoded token
  // Match the exact field names from your backend's tokendata
  return {
    token,
    userId: decodedToken.userId || decodedToken.uId || decodedToken.sub,
    userName: decodedToken.userName || decodedToken.name || decodedToken.username,
    roleId: decodedToken.roleId || decodedToken.role,
    staffId: decodedToken.staffId || decodedToken.uId || decodedToken.userId,
    roleName: decodedToken.roleName,
    depId: decodedToken.depId || decodedToken.departmentId,
    depName: decodedToken.depName || decodedToken.departmentName,
    gradeCode: decodedToken.gradeCode || decodedToken.grade,
    emailId: decodedToken.emailId || decodedToken.email
  };
};

/**
 * Authenticate user using URL parameters
 * Stores authentication data in localStorage and cleans URL
 * @param {Object} authData - Authentication data from URL
 * @returns {boolean} Success status
 */
export const authenticateFromUrl = (authData) => {
  if (!authData || !authData.token || !authData.userId) {
    return false;
  }

  try {
    // Store only the authentication token in localStorage
    // All user data will be extracted from the token via getUserDataFromToken()
    localStorage.setItem("authToken", authData.token);
    
    return true;
  } catch (error) {
    console.error("Error storing authentication data:", error);
    return false;
  }
};

/**
 * Clean URL parameters after authentication
 * Removes auth parameters from URL without page reload
 */
export const cleanUrlParams = () => {
  if (window.history && window.history.replaceState) {
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }
};

/**
 * Complete URL authentication flow
 * Extracts, validates, stores auth data, and cleans URL
 * @returns {boolean} Success status
 */
export const handleUrlAuthentication = () => {
  const authData = extractAuthFromUrl();
  
  if (authData) {
    const success = authenticateFromUrl(authData);
    if (success) {
      cleanUrlParams();
      return true;
    }
  }
  
  return false;
};
